import { z } from 'zod';

/** Booleans kommen aus MariaDB als tinyint (0/1) und müssen echte Booleans werden. */
const asBool = (fallback: boolean) =>
  z
    .union([z.boolean(), z.number(), z.null()])
    .transform(value => (value === null ? fallback : Boolean(value)))
    .default(fallback);

/**
 * Geltungsbereich einer Regel (#1283).
 *
 * `all` = alle Rechtsgeschäftsarten, `include` = nur die verknüpften,
 * `exclude` = alle außer den verknüpften. Der Unterschied zwischen `include`
 * und `exclude` ist nicht Bequemlichkeit, sondern Semantik: wird später eine
 * neue Rechtsgeschäftsart angelegt, greift die Regel bei `include` dort nicht,
 * bei `exclude` und `all` automatisch.
 */
export const HEALTH_CHECK_SCOPE_MODES = ['all', 'include', 'exclude'] as const;
export type HealthCheckScopeMode = (typeof HEALTH_CHECK_SCOPE_MODES)[number];

export const HEALTH_CHECK_SCOPE_MODE_LABEL: Record<
  HealthCheckScopeMode,
  string
> = {
  all: 'Alle Rechtsgeschäftsarten',
  include: 'Nur ausgewählte',
  exclude: 'Alle außer den ausgewählten',
};

/**
 * Globale Konfiguration einer Regel. Eine fehlende Zeile bedeutet „Default aus
 * dem Katalog" — deshalb ist jedes Feld außer `RuleKey` optional.
 *
 * `IsActive` ist der Hauptschalter, `ScopeMode` verengt nur innerhalb von
 * aktiv: „nur bei Grundschuld prüfen" heißt `IsActive = true` plus
 * `ScopeMode = 'include'`.
 */
export const healthCheckRuleConfigSchema = z.object({
  idHealthCheckRuleConfig: z.number().default(-1),
  RuleKey: z.string(),
  IsActive: asBool(true),
  /** null = Default-Severity aus dem Katalog. */
  Severity: z.string().nullable().default(null),
  ScopeMode: z.enum(HEALTH_CHECK_SCOPE_MODES).catch('all').default('all'),
  /** JSON-String mit den Parametern der Regel; null solange unkonfiguriert. */
  Params: z.string().nullable().default(null),
});

export type IHealthCheckRuleConfig = z.infer<
  typeof healthCheckRuleConfigSchema
>;

/** Verknüpfung Regel <-> Rechtsgeschäftsart (nur mit `include`/`exclude` relevant). */
export const healthCheckRuleConfigTypeSchema = z.object({
  idHealthCheckRuleConfigType: z.number().default(-1),
  idHealthCheckRuleConfig: z.number(),
  idLegalTransactionType: z.number(),
  LegalTransactionType: z.string().nullable().optional(),
});

export type IHealthCheckRuleConfigType = z.infer<
  typeof healthCheckRuleConfigTypeSchema
>;

/**
 * Abweichung für genau einen Vorgang. Existiert nur bei bewusster Abweichung —
 * fehlt sie, gilt die globale Vorgabe bzw. der Katalog-Default.
 *
 * `RuleTitle` ist transient und wird nicht gespeichert: der Regelkatalog liegt
 * im Frontend, das Backend kennt nur den Schlüssel und braucht den Titel für
 * den Log-Eintrag (#1330).
 */
export const healthCheckRuleOverrideSchema = z.object({
  idHealthCheckRuleLegalTransaction: z.number().default(-1),
  RuleKey: z.string(),
  IsActive: asBool(false),
  idUser: z.number().nullable().default(null),
  FirstName: z.string().nullable().optional(),
  LastName: z.string().nullable().optional(),
  Created_at: z.string().nullable().optional(),
  Updated_at: z.string().nullable().optional(),
  RuleTitle: z.string().optional(),
});

export type IHealthCheckRuleOverride = z.infer<
  typeof healthCheckRuleOverrideSchema
>;
